import { useMarketLocation } from '@e2m-customer-portal/hooks';
import { ErrorComponent, Loading, PageTitle, Toolbar } from '@e2m-customer-portal/ui';
import { ReactElement, ReactNode, Suspense, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import { QuarterSingleSelector } from './quarter-single-selector/QuarterSingleSelector';
import { QuarterlyRevenueTable } from './quarterly-revenue-table/QuarterlyRevenueTable';

export interface QuarterlyRevenueProps {
  marketLocationId: string;
  pageTitleActions?: ReactNode;
  sidebarOffset?: number;
}

export function QuarterlyRevenue({
  marketLocationId,
  pageTitleActions,
  sidebarOffset = 0,
}: Readonly<QuarterlyRevenueProps>): ReactElement<QuarterlyRevenueProps> {
  const [t] = useTranslation();
  const [marketLocation, error] = useMarketLocation(marketLocationId);

  const [selectedDate, setSelectedDate] = useState<Date>(new Date());

  if (!marketLocation || error) {
    throw new Error('Failed to retrieve market location data');
  }

  return (
    <>
      <div className="md:sticky md:z-[1202] md:bg-white" style={{ top: `${sidebarOffset}px` }}>
        <PageTitle
          title={t('REVENUE_REPORT_TABLE_TITLE', { name: marketLocation.externalName || marketLocation.name })}
        />
        <Toolbar
          left={
            <QuarterSingleSelector
              setQuarter={setSelectedDate}
              marketingStart={marketLocation.marketingStart}
              marketingEnd={marketLocation.marketingEnd}
            />
          }
          right={pageTitleActions}
        />
      </div>
      <ErrorBoundary FallbackComponent={ErrorComponent}>
        <Suspense fallback={<Loading />}>
          <QuarterlyRevenueTable
            marketLocationId={marketLocationId}
            selectedDate={selectedDate}
            technologies={marketLocation.technologies}
            sidebarOffset={sidebarOffset}
          />
        </Suspense>
      </ErrorBoundary>
    </>
  );
}
